import { toast } from "sonner"
import type { ApiResponse, RequestOptions } from "@/domain/interfaces/api"
import { API_CONFIG } from "@/infrastructure/config/apiConfig"

class HttpClient {
  private baseURL: string
  private defaultHeaders: Record<string, string>

  constructor(baseURL: string) {
    this.baseURL = baseURL
    this.defaultHeaders = {
      "Content-Type": "application/json",
    }
  }

  private getAuthToken(): string | null {
    return localStorage.getItem("authToken")
  }

  private getHeaders(customHeaders?: Record<string, string>, excludeContentType?: boolean): Record<string, string> {
    const headers = { ...this.defaultHeaders, ...customHeaders }

    // Remove Content-Type if requested (for FormData)
    if (excludeContentType) {
      delete headers['Content-Type']
    }

    const token = this.getAuthToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    return headers
  }

  private isOnline(): boolean {
    return navigator.onLine
  }

  private handleError(error: unknown): ApiResponse<never> {
    // Check if it's a network error
    if (!this.isOnline()) {
      toast.error("أنت غير متصل بالإنترنت", {
        style: {
          background: "#fef2f2",
          border: "1px solid #fecaca",
          color: "#dc2626",
        },
      })
    } else if (error instanceof TypeError && error.message.includes("fetch")) {
      // Network error but online - likely server unreachable
      toast.error("أنت غير متصل بالإنترنت", {
        style: {
          background: "#fef2f2",
          border: "1px solid #fecaca",
          color: "#dc2626",
        },
      })
    }

    return {
      isSuccess: false,
      message: error instanceof Error ? error.message : "Unknown error occurred",
      errors: {},
    } as ApiResponse<never>
  }

  private async request<T>(endpoint: string, options: RequestOptions & { credentials?: RequestCredentials }): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`

    try {
      const config: RequestInit = {
        method: options.method,
        credentials: options.credentials,
      }

      if (options.body && options.method !== "GET") {
        // Check if body is FormData
        if (options.body instanceof FormData) {
          // For FormData, exclude Content-Type completely - let browser set it with boundary
          config.headers = this.getHeaders(options.headers, true)
          config.body = options.body
        } else {
          // For JSON data, use default headers (application/json)
          config.headers = this.getHeaders(options.headers)
          config.body = JSON.stringify(options.body)
        }
      } else {
        // For GET requests or requests without body, use default headers
        config.headers = this.getHeaders(options.headers)
      }

      const response = await fetch(url, config)
      const data: ApiResponse<T> = await response.json()

      if (!data.isSuccess) {
        throw new Error(data.message || "API request failed")
      }

      return data
    } catch (error) {
      return this.handleError(error) as ApiResponse<T>
    }
  }

  async get<T>(endpoint: string, options?: { headers?: Record<string, string>; credentials?: RequestCredentials }): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "GET", ...options })
  }

  async post<T>(endpoint: string, body?: unknown, options?: { headers?: Record<string, string>; credentials?: RequestCredentials }): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "POST", body, ...options })
  }

  async put<T>(endpoint: string, body?: unknown, options?: { headers?: Record<string, string>; credentials?: RequestCredentials }): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "PUT", body, ...options })
  }

  async delete<T>(endpoint: string, body?: unknown, options?: { headers?: Record<string, string>; credentials?: RequestCredentials }): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "DELETE", body, ...options })
  }

  async patch<T>(endpoint: string, body?: unknown, options?: { headers?: Record<string, string>; credentials?: RequestCredentials }): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "PATCH", body, ...options })
  }
}

export const httpClient = new HttpClient(API_CONFIG.BASE_URL)
export default httpClient